<template>
  <div>
    <p v-if="loading" class="products-status">Loading products…</p>
    <p v-else-if="error" class="products-status products-status--error">
      Couldn't load products: {{ error }}
    </p>

    <div v-else class="products-grid">
      <article
        v-for="(print, index) in products"
        :key="print.id"
        class="product-card"
      >
        <router-link :to="`/products/${print.handle}`" class="product-card__media-wrap">
          <div class="product-card__media">
            <img v-if="print.image" :src="print.image" :alt="print.imageAlt" />
            <PlaceholderArt v-else :tone="index" />
            <span v-if="!print.availableForSale" class="badge badge--sold">Sold Out</span>
          </div>
        </router-link>
        <div class="product-card__body">
          <router-link :to="`/products/${print.handle}`" class="product-card__title-link">
            <h3 class="product-card__title">{{ print.title }}</h3>
          </router-link>
          <div class="product-card__footer">
            <span class="product-card__price">{{ formatPrice(print) }}</span>
            <button
              class="underline-link product-card__buy"
              :disabled="!print.availableForSale || cart.loading"
              @click="buyNow(print)"
            >
              {{ print.availableForSale ? 'Add to Cart' : 'Sold Out' }}
            </button>
          </div>
        </div>
      </article>

      <!-- Pads the grid out to `fill` cards while there are fewer products. -->
      <div
        v-for="n in Math.max(fill - products.length, 0)"
        :key="`placeholder-${n}`"
        class="product-card product-card--placeholder"
        aria-hidden="true"
      >
        <div class="product-card__media">
          <PlaceholderArt :tone="products.length + n" />
        </div>
        <div class="product-card__body">
          <p class="product-card__title">Coming soon</p>
          <span class="product-card__price">—</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useCart } from '../shopify/cart'
import { formatMoney } from '../shopify/money'
import PlaceholderArt from './PlaceholderArt.vue'

export default {
  name: 'ProductGrid',

  components: { PlaceholderArt },

  props: {
    products: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    error: { type: String, default: null },
    fill: { type: Number, default: 0 },
  },

  setup() {
    const { state: cart, addToCart, openDrawer } = useCart()
    return { cart, addToCart, openDrawer }
  },

  methods: {
    formatPrice(product) {
      return formatMoney(product.price, product.currency)
    },
    async buyNow(print) {
      if (!print.variantId || !print.availableForSale) return
      try {
        await this.addToCart(print.variantId)
        this.openDrawer()
      } catch {
        // error surfaced via cart.error in the drawer
      }
    },
  },
}
</script>

<style scoped>
.products-status {
  font-size: 1.4rem;
  color: var(--stone);
  padding: 2rem 0;
}

.products-status--error { color: #b3261e; }

.products-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2.4rem;
  row-gap: 4rem;
}

.product-card { display: flex; flex-direction: column; }

.product-card__media-wrap { display: block; }

.product-card__media {
  position: relative;
  aspect-ratio: 4 / 5;
  overflow: hidden;
  background: var(--paper);
}

.product-card__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.product-card:hover .product-card__media img {
  transform: scale(1.05);
}

.badge {
  position: absolute;
  top: 1.2rem;
  left: 1.2rem;
  background: var(--canvas);
  color: var(--ink);
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  padding: 0.5rem 1rem;
}

.badge--sold {
  background: var(--ink);
  color: var(--canvas);
}

.product-card__body {
  padding: 1.4rem 0 0;
}

.product-card__title-link {
  color: inherit;
  display: block;
}

.product-card__title {
  font-size: 2rem;
  font-weight: 500;
  line-height: 1.2;
  margin-bottom: 0.6rem;
  color: var(--ink);
}

.product-card--placeholder .product-card__title { font-weight: 400; color: var(--stone); }
.product-card--placeholder .product-card__price { color: var(--warm-grey); }

.product-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.product-card__price {
  font-size: 1.4rem;
  color: var(--charcoal);
}

.underline-link {
  font-size: 1.3rem;
  color: var(--stone);
  border-bottom: 1px solid var(--linen);
  padding-bottom: 1px;
  transition: color 0.15s, border-color 0.15s;
}
.underline-link:hover { color: var(--ink); border-color: var(--ink); }

.product-card__buy {
  background: none;
  border: none;
  border-bottom: 1px solid var(--linen);
  padding: 0 0 1px;
  font-family: var(--font);
  cursor: pointer;
}
.product-card__buy:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

@media (max-width: 1200px) {
  .products-grid { grid-template-columns: repeat(3, 1fr); }
}

@media (max-width: 960px) {
  .products-grid { grid-template-columns: repeat(2, 1fr); gap: 1.2rem; row-gap: 3.2rem; }
}

@media (max-width: 400px) {
  .products-grid { grid-template-columns: 1fr; }
}
</style>
